/** Path helpers for public help SPA navigation (no Next soft-nav). */

export type HelpSpaTarget =
  | { readonly kind: "center" }
  | { readonly kind: "space"; readonly spaceId: string }
  | { readonly kind: "page"; readonly pageId: string };

/** Route segment of a space overview: `/help/{org}/spaces/{spaceId}`. */
export const HELP_SPACES_SEGMENT = "spaces";

function decodeSegment(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * Parses `/help/{orgSlug}`, `/help/{orgSlug}/{pageId}` or
 * `/help/{orgSlug}/spaces/{spaceId}`. Null = not this org's help center.
 */
export function parseHelpSpaPath(
  pathname: string,
  orgSlug: string,
): HelpSpaTarget | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "help" || parts.length < 2) return null;
  const slugPart = decodeSegment(parts[1] ?? "");
  if (slugPart === null) return null;
  if (slugPart.toLowerCase() !== orgSlug.toLowerCase()) return null;
  if (parts.length === 2) return { kind: "center" };
  if (parts[2] === HELP_SPACES_SEGMENT) {
    // Bare /spaces or deeper paths are no SPA target; let Next answer them.
    if (parts.length !== 4) return null;
    const spaceId = decodeSegment(parts[3] ?? "");
    return spaceId ? { kind: "space", spaceId } : null;
  }
  const rawPageId = parts[2];
  if (!rawPageId) return { kind: "center" };
  const pageId = decodeSegment(rawPageId);
  if (pageId === null) return null;
  if (!pageId) return { kind: "center" };
  return { kind: "page", pageId };
}

export function helpSpaHref(orgSlug: string, target: HelpSpaTarget): string {
  return helpSpaHrefFromBase(`/help/${encodeURIComponent(orgSlug)}`, target);
}

/** Same URL shape under any base, e.g. the portal's `/portal/knowledge`. */
export function helpSpaHrefFromBase(
  base: string,
  target: HelpSpaTarget,
): string {
  if (target.kind === "center") return base;
  if (target.kind === "space") {
    return `${base}/${HELP_SPACES_SEGMENT}/${encodeURIComponent(target.spaceId)}`;
  }
  return `${base}/${target.pageId}`;
}

/** Same target, compared by kind and id. */
export function isSameHelpTarget(
  left: HelpSpaTarget | null,
  right: HelpSpaTarget | null,
): boolean {
  if (!left || !right) return left === right;
  if (left.kind === "center" || right.kind === "center") {
    return left.kind === right.kind;
  }
  if (left.kind === "space" && right.kind === "space") {
    return left.spaceId === right.spaceId;
  }
  if (left.kind === "page" && right.kind === "page") {
    return left.pageId === right.pageId;
  }
  return false;
}

/** True for plain left-clicks that should stay in-app (no new tab / modified click). */
export function isPlainLeftClick(event: {
  readonly button: number;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly defaultPrevented: boolean;
}): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  return true;
}
